import { Link } from 'react-router-dom'
import PageHero from '@/components/PageHero'
import { Reveal, ImageFrame } from '@/components/Reveal'
import { HelixStrand } from '@/components/Helix'

const principles = [
  {
    title: 'Genetic Integrity',
    body: "We preserve genetic diversity and breed within nature's boundaries — no modification, only selection.",
  },
  {
    title: 'Local Adaptation',
    body: "Hybrids selected for Southern African soils, climates, and pressures — not imported answers to foreign questions.",
  },
  {
    title: 'Long-term Thinking',
    body: 'A breeding program measured in decades, not quarters. The work compounds; patience is the method.',
  },
]

export default function About() {
  return (
    <>
      <PageHero eyebrow="About Hylix" title="One breeder, 25 seasons" tall>
        The story of an independent program built outside the corporate seed system — and the
        conviction that carried it.
      </PageHero>

      <section className="py-24 md:py-32" style={{ backgroundColor: '#F3F5F1' }}>
        <div className="container-hx grid gap-14 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
          <ImageFrame
            src="/images/breeding-hands.jpg"
            alt="The founder at work in the breeding nursery"
            ratio="4/5"
            caption="Hand pollination — the daily craft of the program"
          />
          <div>
            <Reveal className="eyebrow">The Founder</Reveal>
            <Reveal as="h2" delay={0.08} className="mt-5 text-[clamp(1.8rem,3.2vw,2.5rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance]">
              A life's work in the rows
            </Reveal>
            <Reveal as="p" delay={0.16} className="mt-7 leading-[1.85]" style={{ color: '#565650' }}>
              [FOUNDER BIO TO BE FILLED — education, career history, when and why Hylix was started,
              key milestones, personal philosophy on breeding.]
            </Reveal>
            <Reveal as="p" delay={0.2} className="mt-5 leading-[1.85]" style={{ color: '#565650' }}>
              What the record already shows: a single breeder, working independently, built and
              maintained a program that corporate seed companies now license, produce, and depend
              on. The germplasm, the data, and the discipline behind them are the product of one
              person's sustained attention over 25 seasons.
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-24 md:py-32" style={{ backgroundColor: '#FAFBF9' }}>
        <div className="pointer-events-none absolute left-[3%] top-16 hidden xl:block">
          <HelixStrand height={520} width={90} turns={2.5} stroke="#8BA872" opacity={0.25} />
        </div>
        <div className="container-hx relative">
          <Reveal className="eyebrow">The Conviction</Reveal>
          <Reveal as="h2" delay={0.08} className="mt-5 max-w-2xl text-[clamp(1.8rem,3.2vw,2.5rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance]">
            Why conventional?
          </Reveal>
          <Reveal as="p" delay={0.16} className="mt-7 max-w-2xl leading-[1.85]" style={{ color: '#565650' }}>
            Hylix was founded on the conviction that conventional breeding — selecting plants
            generation after generation for the traits that matter — produces hybrids that are
            healthier for people, better for the soil, and more sustainable for African agriculture.
            Genetic modification is unnecessary when disciplined selection, local adaptation, and
            patience are applied.
          </Reveal>
          <div className="mt-16 border-t" style={{ borderColor: '#D9DED5' }}>
            {principles.map((p) => (
              <div
                key={p.title}
                className="grid gap-3 border-b py-8 transition-colors duration-500 hover:bg-[#F3F5F1] md:grid-cols-[280px_1fr] md:gap-10 md:px-6"
                style={{ borderColor: '#D9DED5' }}
              >
                <h3 className="text-lg font-medium" style={{ color: '#24391C' }}>
                  {p.title}
                </h3>
                <p className="text-[15px] leading-[1.8]" style={{ color: '#565650' }}>
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-24 md:py-36" style={{ backgroundColor: '#24391C' }}>
        <div className="pointer-events-none absolute right-[6%] top-1/2 hidden -translate-y-1/2 lg:block">
          <HelixStrand height={480} width={100} turns={3} stroke="#8BA872" opacity={0.3} />
        </div>
        <div className="container-hx relative max-w-3xl">
          <Reveal className="eyebrow" style={{ color: '#8BA872' }}>
            Looking Ahead
          </Reveal>
          <Reveal as="h2" delay={0.08} className="mt-5 text-[clamp(1.8rem,3.2vw,2.5rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance] text-[#F3F5F1]">
            Built to continue
          </Reveal>
          <Reveal as="p" delay={0.16} className="mt-7 leading-[1.85] text-[#F3F5F1]/70">
            Hylix was built to outlast any single season. The germplasm is documented, the records
            are complete, and the methods are proven — a program designed to keep delivering for
            the farmers and seed companies who depend on it, for decades to come.
          </Reveal>
          <Reveal delay={0.24} className="mt-10">
            <Link to="/breeding-program" className="btn-outline-cream">
              Explore the Program
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  )
}
